import { Grid, Paper, TextField, MenuItem, Button, Alert } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"; // make sure to install @mui/x-date-pickers
import { useEffect, useState } from "react";
import axiosClient from "../../api/axiosClient";
import Layout from "../../components/Layout";
import { ReportRequest } from "./ReportRequest";
import { ReportResponse } from "./ReportResponse";
import FullPageLoader from "./FullPageLoader";
import { searchGridSx } from "../../utils/responsiveStyles";

export default function Report() {
  //Loading
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // 🔹 Dropdown options (from API)
  const [pumps, setPumps] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [nozzles, setNozzles] = useState([]);

  // 🔹 Search filters
  const [reportType, setReportType] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [pumpId, setPumpId] = useState("");
  const [nozzleId, setNozzleId] = useState("");
  const [fuelId, setFuelId] = useState("");
  const [format, setFormat] = useState("");

  useEffect(() => {
    axiosClient
      .get("/report/get-all-fuel-grade-lookup") // ✅ your backend endpoint for dropdowns
      .then((res) => {
        const data = res.data;
        console.log("device types: " + JSON.stringify(data));
        setFuels(data || []);
      })
      .catch((err) => console.error("Dropdown fetch failed", err));

    axiosClient
      .get("/report/get-all-pump-lookup") // ✅ your backend endpoint for dropdowns
      .then((res) => {
        const data = res.data;
        console.log("device state: " + JSON.stringify(data));
        setPumps(data || []);
      })
      .catch((err) => console.error("Dropdown fetch failed", err));
  }, []);

  // 🔹 Fetch Data
  const fetchData = () => {
    if (reportType === null || reportType.length === 0) {
      setErrorMessage("Please Select Report Type to Generate Report");
      return;
    }
    if (format === null || format.length === 0) {
      setErrorMessage("Please Select Format to Generate Report");
      return;
    }
    const token = localStorage.getItem("token");
    console.log("token: " + token);
    setLoading(true);

    const request = new ReportRequest();

    if (startDate) request.setStartDate(startDate.toISOString());
    if (endDate) request.setEndDate(endDate.toISOString());
    if (pumpId) request.setDeviceType(Number(pumpId));
    if (nozzleId) request.setDeviceNumber(Number(Number(nozzleId)));
    if (fuelId) request.setState(Number(fuelId));
    if (format) request.setFormat(format);

    var url = "";
    if (reportType === "1") {
      url = "/report/pump-transaction-report";
    } else if (reportType === "2") {
      url = "/report/fuel-sales-summary-report";
    } else if (reportType === "3") {
      url = "/report/pump-nozzles-summary-report";
    }

    setErrorMessage("");

    axiosClient
      .post(url, request)
      .then((res) => {
        const reportResponse = new ReportResponse(res.data);
        if (reportResponse?.getOperationResult() && reportResponse.getData()) {
          try {
            const contentType = reportResponse.getContentType();
            // 🔹 Decode Base64 string → binary
            const byteCharacters = atob(reportResponse.getData());
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: contentType });
            const url = URL.createObjectURL(blob);

            // 🔹 Auto-open based on file type
            if (contentType === "application/pdf") {
              window.open(url, "_blank");
            } else if (
              contentType ===
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ) {
              const a = document.createElement("a");
              a.href = url;
              var reportName = "report.xlsx";
              if (reportType === "1") {
                reportName = "Pump Transaction Report.xlsx";
              } else if (reportType === "2") {
                reportName = "Fuel Sales Summary Report.xlsx";
              } else if (reportType === "3") {
                reportName = "Pump Nozzles Summary Report.xlsx";
              }
              a.download = reportName;
              a.click();
              URL.revokeObjectURL(url);
            } else {
              window.open(url, "_blank");
            }
          } catch (err) {
            setErrorMessage("❌ Error downloading report:", err);
          }
        } else {
          setErrorMessage("Didn't Get it");
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  const handlePumpId = (value) => {
    setPumpId(value);
    if (value === "") {
      setNozzleId("");
      setNozzles([]);
    } else {
      axiosClient
        .get("/report/get-all-nozzle-lookup-by-pump/" + value) // ✅ your backend endpoint for dropdowns
        .then((res) => {
          const data = res.data;
          setNozzles(data || null);
        })
        .catch((err) => console.error("Dropdown fetch failed", err));
    }
  };

  return (
    <>
      <FullPageLoader open={loading} text="Generating report..." />
      <Layout title="Reports">
        {/* Search Form */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
          <Grid container sx={searchGridSx}>
            {/* search fields ... */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Report Type"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                fullWidth
                sx={{ minWidth: { sm: 180 } }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">Pump Transaction Report</MenuItem>
                <MenuItem value="2">Fuel Sales Summary Report</MenuItem>
                <MenuItem value="3">Pump Nozzles Summary Report</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DateTimePicker
                label="Start Date"
                value={startDate}
                onChange={(newVal) => setStartDate(newVal)}
                sx={{ width: "100%" }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DateTimePicker
                label="End Date"
                value={endDate}
                onChange={(newVal) => setEndDate(newVal)}
                sx={{ width: "100%" }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Pump"
                value={pumpId}
                onChange={(e) => handlePumpId(e.target.value)}
                fullWidth
                sx={{ minWidth: { sm: 180 } }}
              >
                <MenuItem value="">All</MenuItem>
                {pumps.map((dt, i) => (
                  <MenuItem key={i} value={dt.id}>
                    {dt.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Nozzle"
                value={nozzleId}
                onChange={(e) => setNozzleId(e.target.value)}
                fullWidth
                sx={{ minWidth: { sm: 180 } }}
              >
                <MenuItem value="">All</MenuItem>
                {nozzles.map((dt, i) => (
                  <MenuItem key={i} value={dt.id}>
                    {dt.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Fuel"
                value={fuelId}
                onChange={(e) => setFuelId(e.target.value)}
                fullWidth
                sx={{ minWidth: { sm: 180 } }}
              >
                <MenuItem value="">All</MenuItem>
                {fuels.map((dt, i) => (
                  <MenuItem key={i} value={dt.id}>
                    {dt.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Format"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                fullWidth
                sx={{ minWidth: { sm: 180 } }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="xlsx">EXCEL</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ minWidth: { sm: 180 } }}
                onClick={fetchData}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Paper>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </Layout>
    </>
  );
}
